import { FlatLookupView } from "@/Common/Api/MedfarDictionaries/Dto/Responses/FlatLookupView";
import { LicenceOrganisationRule } from "@/Common/Api/MedfarDictionaries/Dto/Responses/LicenceOrganisationRule";
import { EmployeeTitleHelper } from "@/Common/Helpers/EmployeeTitleHelper";
import { Address } from "@/Common/Models/Contacts/Address";
import { GeographicInfoCountry } from "@/Common/Models/Localization/GeographicInfoCountry";
import { GeographicInfoState } from "@/Common/Models/Localization/GeographicInfoState";
import { LocationMapper } from "./LocationMapper";

interface IFilteredOrganizationRulesParams {
    allOrgRules: LicenceOrganisationRule[],
    sitesAddress: Address[],
    countryCache: GeographicInfoCountry[],
    stateCache: GeographicInfoState[],
    countryAndStateLookups: FlatLookupView[],
    locationMapper: LocationMapper
}

class FilteredOrganizationRules {
    private _orgRulesByCountryAndState: { [countryCode: string]: { [stateCode: string]: LicenceOrganisationRule[] } } = {};
    private orgRulesForNoOrganization: LicenceOrganisationRule[];
    private allOrgRules: LicenceOrganisationRule[];
    private sitesAddress: Address[];
    private locationMapper: LocationMapper;
    private userLicenceOrganizationRule: LicenceOrganisationRule = null;
    private _statesByCountryInEnv: { [key: string]: Set<string> } = {};
    private _envHasLinkedOrgRules: boolean;
    private _envHasNotLinkedOrgRules: boolean;
    private _isUserTitleInAvailableOrganizationRules: boolean = false;

    constructor(params: IFilteredOrganizationRulesParams) {
        this.allOrgRules = params.allOrgRules;
        this.sitesAddress = params.sitesAddress;
        this.locationMapper = new LocationMapper(
            params.countryCache,
            params.stateCache,
            params.countryAndStateLookups
        );
    }

    public get envHasLinkedOrgRules(): boolean {
        return this._envHasLinkedOrgRules;
    }

    public get envHasNotLinkedOrgRules(): boolean {
        return this._envHasNotLinkedOrgRules;
    }

    public get statesByCountryInEnv(): { [key: string]: Set<string> } {
        return this._statesByCountryInEnv;
    }

    public get orgRulesByCountryAndState(): { [countryCode: string]: { [stateCode: string]: LicenceOrganisationRule[] } } {
        return this._orgRulesByCountryAndState;
    }

    public get isUserTitleInAvailableOrganizationRules(): boolean {
        return this._isUserTitleInAvailableOrganizationRules;
    }

    public getCountryCodes(): string[] {
        return Object.keys(this._orgRulesByCountryAndState);
    }

    public getStateCodes(countryCode: string): string[] {
        return Object.keys(this._orgRulesByCountryAndState[countryCode] || {});
    }

    public getDistinctOrganizations(countryCode: string, stateCode: string): LicenceOrganisationRule[] {
        return this._orgRulesByCountryAndState[countryCode][stateCode]
            .filter(rule => !!rule.OrganisationCode)
            .distinctByProperty(p => p.OrganisationCode)
            .sortByProperty(p => p.Acronym);
    }

    public getTitlesOfOrganization(orgCode: string, countryCode: string, stateCode: string): LicenceOrganisationRule[] {
        // Filter only titles linked to the selected org and its corresponding country and state
        let filteredTitles = [];

        if (orgCode && this.getCountryCodes().includes(countryCode) && this.getStateCodes(countryCode).includes(stateCode)) {
            filteredTitles = this._orgRulesByCountryAndState[countryCode][stateCode] ?? [];
            filteredTitles = filteredTitles.filter(rule => rule.OrganisationCode == orgCode);
        }

        return EmployeeTitleHelper.sortTitleOrder(filteredTitles);
    }

    public getNoOrganizationTitles(): LicenceOrganisationRule[] {
        let filteredTitles = this.orgRulesForNoOrganization
            .filter(p => this.statesByCountryInEnv[p.CountryCode]?.has(p.StateCode) || (p.CountryCode == "" && p.StateCode == ""));

        // If there are only site locations that are mapped to organizations in the environment, only show non legacy titles
        if (this._envHasLinkedOrgRules && !this._envHasNotLinkedOrgRules) {
            filteredTitles = filteredTitles.filter(p => !p.IsLegacy);
        }

        return EmployeeTitleHelper.sortTitleOrder(filteredTitles);
    }

    public getCountryLabel(twoLetterIsoCountryCode: string): string {
        return this.locationMapper.getCountryLookup(twoLetterIsoCountryCode).GetValue() ?? twoLetterIsoCountryCode;
    }

    public getStateLabel(twoLetterIsoCountryCode: string, twoLetterIsoStateCode: string): string {
        return this.locationMapper.getStateLookup(twoLetterIsoCountryCode, twoLetterIsoStateCode).GetValue() ?? twoLetterIsoStateCode;
    }

    /**
     * Set dictionnary of state codes by country codes of siteModels adresses
     */
    private setCountriesStatesEnvironment(): void {
        for (const address of this.sitesAddress) {
            const stateCode = this.locationMapper.getStateCode(address.StateLookupId);
            const countryCode = this.locationMapper.getCountryCode(address.CountryLookupId);

            if (stateCode) {
                if (!this.statesByCountryInEnv[countryCode])
                    this.statesByCountryInEnv[countryCode] = new Set<string>();

                this.statesByCountryInEnv[countryCode]
                    .add(stateCode);
            }
        }
    }

    /**
     * Set if the environment contains:
     *  - Associated licence org rules
     *  - Not associated org rules
     */
    private setEnvContainsLicenceOrgRule(): void {
        this._envHasLinkedOrgRules = false;
        this._envHasNotLinkedOrgRules = false;

        const countriesInEnv = Object.keys(this.statesByCountryInEnv);

        for (const country of countriesInEnv) {
            for (const state of Array.from(this.statesByCountryInEnv[country])) {
                this.getStateCodes(country).includes(state)
                    ? this._envHasLinkedOrgRules = true // Found environment site associated with org rule
                    : this._envHasNotLinkedOrgRules = true; // Found environment site not associated with org rule

                if (this._envHasLinkedOrgRules && this._envHasNotLinkedOrgRules)
                    return;
            }
        }
    }

    public createLicenceOrganizationRulesByCountryAndState(
        userLicenceOrganizationRule: LicenceOrganisationRule | null,
        activeSiteCountryLookupId: string = null,
        activeSiteStateLookupId: string = null,
        includeInactiveRules: boolean = false
    ): FilteredOrganizationRules {
        this.setCountriesStatesEnvironment();

        // Create initial list of all possible organization rules
        const licenceOrganizationRules = this.allOrgRules
            .filter(rule => includeInactiveRules || rule.isActive)
            .filter(rule => this.statesByCountryInEnv[rule.CountryCode]?.has(rule.StateCode));

        // Initialize list of licence organization rules without organization
        this.orgRulesForNoOrganization = this.allOrgRules.filter(rule => !rule.OrganisationCode);

        // Initialize OrganizationRulesDictionaryByCountryAndState
        let organizationsByCountryAndState: { [countryCode: string]: { [stateCode: string]: LicenceOrganisationRule[] } } = {};

        // Sorted dictionnary, by country
        const organizationsByCountry = licenceOrganizationRules
            .toLookup(p => p.CountryCode);
        const sortedCountryKeys = Object.keys(organizationsByCountry)
            .map(countryCode => ({
                countryCode: countryCode,
                countryLookup: this.locationMapper.getCountryLookup(countryCode)
            }))
            .sortByProperty(countryDto => countryDto.countryLookup.GetValue())
            .sortByPropertyDesc(countryDto => countryDto.countryLookup.LookupID === activeSiteCountryLookupId);

        for (const countryDto of sortedCountryKeys) {
            // Initialize dictionnary for country
            organizationsByCountryAndState[countryDto.countryCode] = {};

            // Sort states
            const organizationsByState = organizationsByCountry[countryDto.countryCode].toLookup(p => p.StateCode);
            let sortedStates = Object.keys(organizationsByState)
                .map(stateCode => ({
                    stateCode: stateCode,
                    stateLookup: this.locationMapper.getStateLookup(countryDto.countryCode, stateCode)
                }))
                .sortByProperty(dto => dto.stateLookup?.GetValue())
                .sortByPropertyDesc(dto => dto.stateLookup?.LookupID === activeSiteStateLookupId);

            // Insert organizationsByState in sorted order.
            for (const stateDto of sortedStates)
                organizationsByCountryAndState[countryDto.countryCode][stateDto.stateCode] = organizationsByState[stateDto.stateCode];
        }

        // Add currently selected license organization rule
        if (userLicenceOrganizationRule?.CountryCode && userLicenceOrganizationRule?.StateCode) {
            this.userLicenceOrganizationRule = userLicenceOrganizationRule;

            if (!organizationsByCountryAndState[userLicenceOrganizationRule.CountryCode]) {
                organizationsByCountryAndState[userLicenceOrganizationRule.CountryCode] = {};
            }

            const organizationsRules = organizationsByCountryAndState[userLicenceOrganizationRule.CountryCode][userLicenceOrganizationRule.StateCode];

            if (!organizationsRules?.any(p => p.Id === userLicenceOrganizationRule.Id)) {
                organizationsByCountryAndState[userLicenceOrganizationRule.CountryCode][userLicenceOrganizationRule.StateCode] = [
                    ...organizationsRules ?? [],
                    userLicenceOrganizationRule
                ];
            }
            else {
                this._isUserTitleInAvailableOrganizationRules = true;
            }
        }

        this._orgRulesByCountryAndState = organizationsByCountryAndState;
        this.setEnvContainsLicenceOrgRule();

        return this;
    }

    public isOrganizationInAvailableOrganizationRules(title: LicenceOrganisationRule): boolean {
        if (!title)
            return false;

        if (this.getTitlesOfOrganization(title.OrganisationCode, title.CountryCode, title.StateCode).includes(title)
            || this.getNoOrganizationTitles().includes(title))
            return true;

        return title.Id === this.userLicenceOrganizationRule?.Id;
    }

    public getOrganizationAcronymAndNameById(id: string): string {
        const parts = id?.split("_");
        if (!parts || parts.length < 3)
            return "";

        const model = this.getTitlesOfOrganization(parts[2], parts[0], parts[1]).firstOrDefault();
        return model
            ? LicenceOrganisationRule.getOrganizationAcronymAndName(model)
            : "";
    }

    public getOrganizationAcronymAndNameByCode(organizationCode: string, countryCode: string, stateCode: string): string {
        const model = this.getTitlesOfOrganization(organizationCode, countryCode, stateCode).firstOrDefault();
        if (!model)
            return "";

        return LicenceOrganisationRule.getOrganizationAcronymAndName(model);
    }
}

export { FilteredOrganizationRules, IFilteredOrganizationRulesParams };
